import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { Separator } from "./separator";
import { Textarea } from "./textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Check, Palette, Maximize2, Frame,
  FileText, Send, Paintbrush, Home, Building2, Sparkles
} from "lucide-react";
import { API } from "../../apiConfig";

const STEPS = [
  { id: "artwork", label: "Artwork", icon: Paintbrush },
  { id: "substrate", label: "Material", icon: Palette },
  { id: "size", label: "Size", icon: Maximize2 },
  { id: "framing", label: "Framing", icon: Frame },
  { id: "details", label: "Details", icon: Sparkles },
  { id: "contact", label: "Contact", icon: FileText },
];

const SUBSTRATES = [
  { value: "archival_paper", label: "Archival Giclée Paper", desc: "Museum-grade, 310gsm cotton rag. Rich detail and true-to-original colour reproduction.", icon: "📜" },
  { value: "canvas", label: "Stretched Canvas", desc: "Gallery-wrap on solid timber stretcher bars. Hand-stretched and ready to hang.", icon: "🖼" },
  { value: "metal", label: "Aluminium Dibond", desc: "Vivid colours with an ultra-modern, luminous finish. Exceptionally durable.", icon: "✨" },
  { value: "acrylic", label: "Acrylic Face Mount", desc: "Glass-like depth and vibrancy. A frameless, contemporary statement piece.", icon: "💎" },
  { value: "wood", label: "Natural Timber Panel", desc: "Printed directly onto sustainably sourced hardwood. Warm, textured, and organic.", icon: "🪵" },
];

const SIZES = [
  { value: "40x30cm", label: "40 × 30 cm", desc: "Intimate — desk or shelf display" },
  { value: "60x45cm", label: "60 × 45 cm", desc: "Classic — bedside or hallway" },
  { value: "80x60cm", label: "80 × 60 cm", desc: "Statement — living room focal point" },
  { value: "100x75cm", label: "100 × 75 cm", desc: "Grand — dining room or office centrepiece" },
  { value: "120x90cm", label: "120 × 90 cm", desc: "Commanding — feature wall" },
  { value: "150x100cm", label: "150 × 100 cm", desc: "Mural — gallery or commercial space" },
  { value: "custom", label: "Custom Dimensions", desc: "Tell us your exact requirements" },
];

const FRAMING_OPTIONS = [
  { value: "unframed", label: "Unframed", desc: "Ready for your own framing or frameless display" },
  { value: "black_gallery", label: "Black Gallery Frame", desc: "Classic gallery presentation, clean lines" },
  { value: "white_gallery", label: "White Gallery Frame", desc: "Light, contemporary aesthetic" },
  { value: "natural_timber", label: "Natural Timber Frame", desc: "Warm hardwood, complements Indigenous art" },
  { value: "float_frame", label: "Float Frame", desc: "Artwork appears to hover within the frame" },
  { value: "shadow_box", label: "Shadow Box Frame", desc: "Deep frame with museum-quality spacing" },
  { value: "custom", label: "Custom Framing", desc: "Describe your vision and we'll match it" },
];

const INTENDED_USE = [
  { value: "personal_home", label: "Personal Home", icon: Home },
  { value: "corporate_office", label: "Corporate / Office", icon: Building2 },
  { value: "gallery_exhibition", label: "Gallery / Exhibition", icon: Paintbrush },
  { value: "gift", label: "Gift", icon: Sparkles },
  { value: "hospitality", label: "Hotel / Hospitality", icon: Building2 },
  { value: "other", label: "Other", icon: FileText },
];

const QuoteBuilder = ({ open, onClose, artworkTitle, productId, artworkImage }) => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    artwork_title: artworkTitle || "",
    custom_artwork_desc: "",
    substrate: "",
    size: "",
    custom_size: "",
    framing: "",
    custom_framing_notes: "",
    intended_use: "",
    room_description: "",
    style_preferences: "",
    urgency: "standard",
    budget_range: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canProceed = () => {
    switch (STEPS[step].id) {
      case "artwork": return form.artwork_title || form.custom_artwork_desc;
      case "substrate": return form.substrate;
      case "size": return form.size && (form.size !== "custom" || form.custom_size);
      case "framing": return form.framing;
      case "details": return true;
      case "contact": return form.name && form.email;
      default: return true;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        artwork_title: form.artwork_title || "Custom Commission",
        product_id: productId || null,
        substrate: form.substrate,
        size: form.size === "custom" ? form.custom_size : form.size,
        framing: form.framing === "custom" ? form.custom_framing_notes : form.framing,
        name: form.name,
        email: form.email,
        phone: form.phone,
        notes: [
          form.custom_artwork_desc && `Artwork description: ${form.custom_artwork_desc}`,
          form.intended_use && `Intended use: ${form.intended_use}`,
          form.room_description && `Space: ${form.room_description}`,
          form.style_preferences && `Style: ${form.style_preferences}`,
          form.urgency !== "standard" && `Timeline: ${form.urgency}`,
          form.budget_range && `Budget: ${form.budget_range}`,
          form.notes && `Notes: ${form.notes}`,
        ].filter(Boolean).join("\n"),
      });
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setSubmitted(false);
    setForm({
      artwork_title: artworkTitle || "", custom_artwork_desc: "", substrate: "", size: "",
      custom_size: "", framing: "", custom_framing_notes: "", intended_use: "",
      room_description: "", style_preferences: "", urgency: "standard",
      budget_range: "", name: "", email: "", phone: "", notes: "",
    });
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-y-auto" data-testid="quote-builder">
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between sticky top-0 bg-background/95 backdrop-blur-sm z-10">
          <div>
            <p className="text-xs tracking-widest uppercase text-muted-foreground">Quote Builder</p>
            <h2 className="text-lg font-medium">
              {artworkTitle ? <span className="italic">{artworkTitle}</span> : "Custom Commission"}
            </h2>
          </div>
          <Button variant="ghost" onClick={handleClose} className="rounded-full" data-testid="close-quote-builder">
            Close
          </Button>
        </div>

        {/* Progress */}
        {!submitted && (
          <div className="px-6 py-4 border-b border-border">
            <div className="max-w-3xl mx-auto flex items-center gap-2">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.id} className="flex items-center flex-1">
                    <button
                      onClick={() => i <= step && setStep(i)}
                      className={`flex items-center gap-2 text-sm transition-colors ${
                        i === step
                          ? "text-primary font-medium"
                          : i < step
                          ? "text-emerald-500 cursor-pointer"
                          : "text-muted-foreground/50"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs border transition-colors ${
                        i === step
                          ? "border-primary bg-primary text-primary-foreground"
                          : i < step
                          ? "border-emerald-500 bg-emerald-500 text-white"
                          : "border-border"
                      }`}>
                        {i < step ? <Check className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}
                      </div>
                      <span className="hidden md:inline">{s.label}</span>
                    </button>
                    {i < STEPS.length - 1 && (
                      <div className={`flex-1 h-px mx-2 ${i < step ? "bg-emerald-500" : "bg-border"}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 px-6 py-8 md:py-12">
          <div className="max-w-2xl mx-auto">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                >
                  <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
                    <Send className="h-10 w-10 text-emerald-500" />
                  </div>
                  <h2 className="text-2xl font-medium mb-3">Your Quote Request is With Us</h2>
                  <p className="text-muted-foreground max-w-md mx-auto mb-8">
                    Our team will review your creative brief and send you a detailed quote 
                    within 24-48 hours. We'll be in touch at <strong>{form.email}</strong>.
                  </p>
                  <Button onClick={handleClose} size="lg" className="rounded-xl" data-testid="quote-done-btn">
                    Return to Gallery
                  </Button>
                </motion.div>
              ) : (
                <motion.div
                  key={STEPS[step].id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Step: Artwork */}
                  {STEPS[step].id === "artwork" && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-2xl font-medium mb-2">Which artwork interests you?</h3>
                        <p className="text-muted-foreground">Select from our collection or describe a custom commission.</p>
                      </div>
                      {artworkTitle ? (
                        <div className="flex items-center gap-4 p-4 border border-primary/30 bg-primary/5 rounded-xl">
                          {artworkImage && (
                            <img src={artworkImage} alt={artworkTitle} className="w-20 h-20 object-cover rounded-lg" />
                          )}
                          <div>
                            <p className="font-medium italic">{artworkTitle}</p>
                            <p className="text-sm text-muted-foreground">From The White Wallaby Collection</p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <Input
                            placeholder="Artwork name (if you have one in mind)"
                            value={form.artwork_title}
                            onChange={(e) => update("artwork_title", e.target.value)}
                            className="rounded-xl h-12"
                            data-testid="quote-artwork-title"
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="text-muted-foreground text-sm">Or describe what you're looking for:</Label>
                        <Textarea
                          placeholder="I'm looking for a piece that captures the essence of the Australian landscape... or I'd like something similar to [artwork name] but in cooler tones..."
                          value={form.custom_artwork_desc}
                          onChange={(e) => update("custom_artwork_desc", e.target.value)}
                          className="rounded-xl min-h-[120px]"
                          data-testid="quote-artwork-desc"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step: Substrate */}
                  {STEPS[step].id === "substrate" && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-2xl font-medium mb-2">Choose your substrate</h3>
                        <p className="text-muted-foreground">The material your artwork is printed on dramatically changes its character.</p>
                      </div>
                      <div className="space-y-3">
                        {SUBSTRATES.map((s) => (
                          <button
                            key={s.value}
                            type="button"
                            onClick={() => update("substrate", s.value)}
                            className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
                              form.substrate === s.value
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/30"
                            }`}
                            data-testid={`quote-substrate-${s.value}`}
                          >
                            <div className="flex items-start gap-4">
                              <span className="text-2xl mt-0.5">{s.icon}</span>
                              <div>
                                <p className="font-medium">{s.label}</p>
                                <p className="text-sm text-muted-foreground mt-1">{s.desc}</p>
                              </div>
                              {form.substrate === s.value && (
                                <Check className="h-5 w-5 text-primary ml-auto shrink-0 mt-1" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step: Size */}
                  {STEPS[step].id === "size" && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-2xl font-medium mb-2">Select your size</h3>
                        <p className="text-muted-foreground">Consider where the piece will live and the impact you want it to make.</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {SIZES.map((s) => (
                          <button
                            key={s.value}
                            type="button"
                            onClick={() => update("size", s.value)}
                            className={`text-left p-4 rounded-xl border-2 transition-all ${
                              form.size === s.value
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/30"
                            }`}
                            data-testid={`quote-size-${s.value}`}
                          >
                            <p className="font-medium">{s.label}</p>
                            <p className="text-xs text-muted-foreground mt-1">{s.desc}</p>
                          </button>
                        ))}
                      </div>
                      {form.size === "custom" && (
                        <Input
                          placeholder="Enter your dimensions (e.g. 200 × 140 cm)"
                          value={form.custom_size}
                          onChange={(e) => update("custom_size", e.target.value)}
                          className="rounded-xl h-12"
                          data-testid="quote-custom-size"
                        />
                      )}
                    </div>
                  )}

                  {/* Step: Framing */}
                  {STEPS[step].id === "framing" && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-2xl font-medium mb-2">Framing preference</h3>
                        <p className="text-muted-foreground">The right frame elevates the artwork and your space.</p>
                      </div>
                      <div className="space-y-3">
                        {FRAMING_OPTIONS.map((f) => (
                          <button
                            key={f.value}
                            type="button"
                            onClick={() => update("framing", f.value)}
                            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                              form.framing === f.value
                                ? "border-primary bg-primary/5"
                                : "border-border hover:border-primary/30"
                            }`}
                            data-testid={`quote-framing-${f.value}`}
                          >
                            <p className="font-medium">{f.label}</p>
                            <p className="text-sm text-muted-foreground mt-1">{f.desc}</p>
                          </button>
                        ))}
                      </div>
                      {form.framing === "custom" && (
                        <Textarea
                          placeholder="Describe your framing vision..."
                          value={form.custom_framing_notes}
                          onChange={(e) => update("custom_framing_notes", e.target.value)}
                          className="rounded-xl"
                          data-testid="quote-custom-framing"
                        />
                      )}
                    </div>
                  )}

                  {/* Step: Details */}
                  {STEPS[step].id === "details" && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-2xl font-medium mb-2">Tell us about your space</h3>
                        <p className="text-muted-foreground">This helps us tailor our recommendation to your environment.</p>
                      </div>

                      <div className="space-y-2">
                        <Label>What is this piece for?</Label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {INTENDED_USE.map((u) => {
                            const Icon = u.icon;
                            return (
                              <button
                                key={u.value}
                                type="button"
                                onClick={() => update("intended_use", u.value)}
                                className={`p-3 rounded-xl border-2 text-center transition-all ${
                                  form.intended_use === u.value
                                    ? "border-primary bg-primary/5"
                                    : "border-border hover:border-primary/30"
                                }`}
                                data-testid={`quote-use-${u.value}`}
                              >
                                <Icon className="h-5 w-5 mx-auto mb-1.5 text-muted-foreground" />
                                <p className="text-sm font-medium">{u.label}</p>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Describe the space (optional)</Label>
                        <Textarea
                          placeholder="e.g. Large open-plan living room with white walls, natural light from the north..."
                          value={form.room_description}
                          onChange={(e) => update("room_description", e.target.value)}
                          className="rounded-xl"
                          data-testid="quote-room-desc"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Style preferences (optional)</Label>
                        <Textarea
                          placeholder="e.g. Earthy tones, minimal framing, something that connects to the land..."
                          value={form.style_preferences}
                          onChange={(e) => update("style_preferences", e.target.value)}
                          className="rounded-xl"
                          data-testid="quote-style"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Timeline</Label>
                          <Select value={form.urgency} onValueChange={(v) => update("urgency", v)}>
                            <SelectTrigger className="rounded-xl" data-testid="quote-urgency">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="standard">Standard (2-4 weeks)</SelectItem>
                              <SelectItem value="priority">Priority (1-2 weeks)</SelectItem>
                              <SelectItem value="rush">Rush (under 1 week)</SelectItem>
                              <SelectItem value="flexible">No rush, take your time</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Budget range (optional)</Label>
                          <Select value={form.budget_range} onValueChange={(v) => update("budget_range", v)}>
                            <SelectTrigger className="rounded-xl" data-testid="quote-budget">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="under_1000">Under $1,000</SelectItem>
                              <SelectItem value="1000_3000">$1,000 – $3,000</SelectItem>
                              <SelectItem value="3000_5000">$3,000 – $5,000</SelectItem>
                              <SelectItem value="5000_10000">$5,000 – $10,000</SelectItem>
                              <SelectItem value="over_10000">$10,000+</SelectItem>
                              <SelectItem value="open">Open / Flexible</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step: Contact */}
                  {STEPS[step].id === "contact" && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-2xl font-medium mb-2">How can we reach you?</h3>
                        <p className="text-muted-foreground">We'll send your custom quote to the email address below.</p>
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Full name *</Label>
                          <Input
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            className="rounded-xl h-12"
                            required
                            data-testid="quote-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email address *</Label>
                          <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            className="rounded-xl h-12"
                            required
                            data-testid="quote-email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Phone (optional)</Label>
                          <Input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => update("phone", e.target.value)}
                            className="rounded-xl h-12"
                            data-testid="quote-phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Anything else? (optional)</Label>
                          <Textarea
                            placeholder="Additional requirements, reference images, or questions..."
                            value={form.notes}
                            onChange={(e) => update("notes", e.target.value)}
                            className="rounded-xl min-h-[100px]"
                            data-testid="quote-notes"
                          />
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                        <p className="text-sm font-medium">Your Brief:</p>
                        <div className="text-sm text-muted-foreground space-y-1">
                          {form.artwork_title && <p>Artwork: <span className="text-foreground italic">{form.artwork_title}</span></p>}
                          {form.substrate && <p>Material: <span className="text-foreground">{SUBSTRATES.find(s => s.value === form.substrate)?.label}</span></p>}
                          {form.size && <p>Size: <span className="text-foreground">{form.size === "custom" ? form.custom_size : SIZES.find(s => s.value === form.size)?.label}</span></p>}
                          {form.framing && <p>Framing: <span className="text-foreground">{FRAMING_OPTIONS.find(f => f.value === form.framing)?.label}</span></p>}
                          {form.intended_use && <p>For: <span className="text-foreground">{INTENDED_USE.find(u => u.value === form.intended_use)?.label}</span></p>}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Footer nav */}
        {!submitted && (
          <div className="border-t border-border px-6 py-4 sticky bottom-0 bg-background/95 backdrop-blur-sm">
            <div className="max-w-2xl mx-auto flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
                className="rounded-xl"
                data-testid="quote-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>

              <p className="text-sm text-muted-foreground">
                {step + 1} of {STEPS.length}
              </p>

              {step < STEPS.length - 1 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="rounded-xl"
                  data-testid="quote-next-btn"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || submitting}
                  className="rounded-xl"
                  data-testid="quote-submit-btn"
                >
                  {submitting ? "Submitting..." : "Submit Quote Request"}
                  <Send className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteBuilder;
